import { useParams, useSearchParams } from 'react-router-dom';
import { Container } from '@vnetwork/ui/layout';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { Badge } from '@vnetwork/ui/primitives';
import { useContractNegotiationDetail } from '@/api/contract-negotiations';
import { DetailHeader } from '@/components/detail-header';
import { TabError, TabLoading } from '@/pages/customers/detail/tab-states';
import { StatusPill } from '@vnetwork/ui/data';
import {
  negotiationStatusTone,
  negotiationStatusLabel,
  negotiationPriorityTone,
  negotiationPriorityLabel,
} from '../negotiation-format';
import { NegotiationInfoCard } from './negotiation-info-card';
import { NegotiationHistoryTab } from './negotiation-history-tab';
import { NegotiationActions } from './negotiation-actions';

const TABS = [
  { value: 'history', label: 'History' },
] as const;

const TAB_VALUES = TABS.map((t) => t.value) as readonly string[];

/**
 * Contract Negotiation detail page — identity header (code + status/priority pills)
 * over the full facts card and a History tab. Mirrors the legacy Negotiation detail
 * layout on @vnetwork/ui v2 + DetailHeader canonical archetype. The archived-banner
 * pattern from the legacy Archived component is inlined here as an Alert. Tab state
 * is persisted in ?tab= search param (lazy mount).
 */
export default function ContractNegotiationDetailPage() {
  const { id: idParam } = useParams();
  const id = Number(idParam) || 0;
  const [searchParams, setSearchParams] = useSearchParams();

  const requestedTab = searchParams.get('tab');
  const activeTab = requestedTab && TAB_VALUES.includes(requestedTab) ? requestedTab : 'history';

  const { data, isLoading, isError, refetch } = useContractNegotiationDetail(id);
  const detail = data?.result;

  const onTabChange = (value: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', value);
    setSearchParams(next, { replace: true });
  };

  return (
    <Container size="full">
      {isError ? (
        <TabError title="Failed to load negotiation" onRetry={() => refetch()} />
      ) : isLoading || !detail ? (
        <TabLoading rows={4} />
      ) : (
        <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          <DetailHeader
            parent={{ label: 'Contract Negotiations', to: '/contracts/negotiations' }}
            current={detail.contract_negotiation_code}
            idPrefix={`#${detail.contract_negotiation_id}`}
            title={detail.contract_negotiation_title || detail.contract_negotiation_code}
            meta={
              <>
                <StatusPill tone={negotiationStatusTone(detail.contract_negotiation_status)}>
                  {negotiationStatusLabel(detail.contract_negotiation_status)}
                </StatusPill>
                {detail.contract_negotiation_priority ? (
                  <Badge
                    appearance="subtle"
                    tone={negotiationPriorityTone(detail.contract_negotiation_priority)}
                  >
                    {negotiationPriorityLabel(detail.contract_negotiation_priority)}
                  </Badge>
                ) : null}
                {detail.archived ? (
                  <Badge appearance="subtle" tone="neutral">
                    Archived
                  </Badge>
                ) : null}
              </>
            }
          />

          {detail.archived ? (
            <Alert tone="warning" title="This negotiation is archived">
              {detail.archived_at
                ? `Archived on ${new Date(detail.archived_at).toLocaleDateString('en-GB')}.`
                : 'This negotiation has been archived.'}
            </Alert>
          ) : null}

          <NegotiationActions detail={detail} />

          <NegotiationInfoCard detail={detail} />

          <Tabs value={activeTab} onValueChange={onTabChange}>
            <TabsList>
              {TABS.map((tab) => (
                <TabsTrigger key={tab.value} value={tab.value}>
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>

            <TabsContent value="history">
              {activeTab === 'history' ? (
                <NegotiationHistoryTab negotiationId={detail.contract_negotiation_id} />
              ) : null}
            </TabsContent>
          </Tabs>
        </div>
      )}
    </Container>
  );
}
